import React, { useEffect, useState } from 'react';
import { getAvailableTriggers } from '../hooks/useVersionTrigger';
import { getAppConfig, getAppName, isDevelopmentMode } from '../utils/config';
import { getForceUpdateState } from '../utils/forceUpdateDetection';
import { getUpdateDetectionStatus } from '../utils/updateDetection';
import { formatVersion, getCurrentVersionInfo, parseVersion } from '../utils/version';

interface VersionPopupProps {
  isOpen: boolean;
  onClose: () => void;
  className?: string;
}

export const VersionPopup: React.FC<VersionPopupProps> = ({
  isOpen,
  onClose,
  className = ''
}) => {
  const [updateStatus, setUpdateStatus] = useState(getUpdateDetectionStatus());
  const [forceUpdateState, setForceUpdateState] = useState(getForceUpdateState());
  const [availableTriggers] = useState(getAvailableTriggers());

  const versionInfo = getCurrentVersionInfo();
  const appName = getAppName();
  const config = getAppConfig();

  // Update status periodically
  useEffect(() => {
    if (!isOpen) return;

    const interval = setInterval(() => {
      setUpdateStatus(getUpdateDetectionStatus());
      setForceUpdateState(getForceUpdateState());
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const parsedVersion = parseVersion(versionInfo.version);
  const isDev = isDevelopmentMode();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`
        bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto
        ${className}
      `}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900">Version Information</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* App Info */}
          <div className="space-y-2">
            <h3 className="font-semibold text-gray-900">{appName}</h3>
            <div className="text-sm text-gray-600">
              <div>Version: {formatVersion(versionInfo.version)}</div>
              <div>Build: {versionInfo.buildNumber}</div>
              <div>Environment: {versionInfo.environment.toUpperCase()}</div>
              <div>Build Date: {new Date(versionInfo.buildDate).toLocaleString()}</div>
            </div>
          </div>

          {/* Version Details */}
          <div className="bg-gray-50 rounded p-3">
            <h4 className="font-medium text-gray-900 mb-2">Version Details</h4>
            <div className="text-sm text-gray-600 space-y-1">
              <div>Major: {parsedVersion.major}</div>
              <div>Minor: {parsedVersion.minor}</div>
              <div>Patch: {parsedVersion.patch}</div>
              {parsedVersion.build && <div>Build: {parsedVersion.build}</div>}
            </div>
          </div>

          {/* Update Status */}
          <div className="bg-blue-50 rounded p-3">
            <h4 className="font-medium text-gray-900 mb-2">Update Status</h4>
            <div className="text-sm text-gray-600 space-y-1">
              <div>Detection Running: {updateStatus.isRunning ? 'Yes' : 'No'}</div>
              <div>Currently Checking: {updateStatus.isChecking ? 'Yes' : 'No'}</div>
              {forceUpdateState && (
                <div>App Blocked: {forceUpdateState.isBlocked ? 'Yes' : 'No'}</div>
              )}
            </div>
          </div>

          {/* Configuration */}
          <div className="bg-green-50 rounded p-3">
            <h4 className="font-medium text-gray-900 mb-2">Configuration</h4>
            <div className="text-sm text-gray-600 space-y-1">
              <div>Force Update: {config.forceUpdateEnabled ? 'Enabled' : 'Disabled'}</div>
              <div>Developer Mode: {config.developerMode ? 'Enabled' : 'Disabled'}</div>
              <div>Check Interval: {config.updateCheckInterval / 1000}s</div>
              <div>Minimum Version: {config.minimumRequiredVersion}</div>
            </div>
          </div>

          {/* Available Triggers */}
          {availableTriggers.length > 0 && (
            <div className="bg-yellow-50 rounded p-3">
              <h4 className="font-medium text-gray-900 mb-2">Available Triggers</h4>
              <div className="text-sm text-gray-600 space-y-1">
                {availableTriggers.map((trigger, index) => (
                  <div key={index}>• {trigger}</div>
                ))}
              </div>
            </div>
          )}

          {/* Development Info */}
          {isDev && (
            <div className="bg-purple-50 rounded p-3">
              <h4 className="font-medium text-gray-900 mb-2">Development Info</h4>
              <div className="text-sm text-gray-600 space-y-1">
                <div>Node Env: {process.env.NODE_ENV}</div>
                <div>React Version: {React.version}</div>
                <div>Build Time: {new Date().toLocaleString()}</div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default VersionPopup; 